import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { isPast } from 'date-fns';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_PLAYERS_SLUG_CARDS } from '@sorare/core/src/constants/__generated__/routes';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import { getShortPlayerName } from '@sorare/core/src/lib/players';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import { ProbablePitcher } from '@sorare/marketplace/src/shared/player/ProbablePitcher';

import {
  StatsTableCell as Cell,
  StatsTableRow,
} from '@sorare/gameplay/src/atoms/StatsTable';
import { StartingPitcher } from '@sorare/gameplay/src/components/so5/ComposeTeam/responsive/StartingPitcher';

import { GameStatsCells } from './GameStatsCells';
import type {
  PlayerRow_BaseballPlayer,
  PlayerRow_GameOfBaseball,
  PlayerRow_So5Appearance,
} from './__generated__/index.graphql';

const PlayerNameLink = styled(Link)`
  color: var(--c-primary-900);
  text-align: left;
  white-space: nowrap;

  display: flex;
  align-items: center;
  gap: var(--double-unit);
`;

type Props = {
  fantasyScore?: boolean;
  game: PlayerRow_GameOfBaseball;
  position?: 'pitching' | 'batting';
  player: PlayerRow_BaseballPlayer;
};

export const PlayerRow = ({ position, game, fantasyScore, player }: Props) => {
  const isDesktopAndAbove = useIsDesktopAndAbove();
  const isStartingPitcher =
    isPast(game.date) &&
    game.probablePitchers.some(pp => pp.slug === player.slug);
  const isProbablePitcher =
    !isPast(game.date) &&
    game.probablePitchers.some(pp => pp.slug === player.slug);

  return (
    <StatsTableRow key={game.id}>
      <Cell>
        <Horizontal>
          <PlayerNameLink
            to={generateSportPath(ANY_SPORT_PLAYERS_SLUG_CARDS, {
              params: { slug: player.slug },
              sport: Sport.BASEBALL,
            })}
          >
            <LabelM>
              {isDesktopAndAbove
                ? player.displayName
                : getShortPlayerName(player.displayName)}
            </LabelM>
            <span>
              {!!isProbablePitcher && <ProbablePitcher variant="chip" />}
              {!!isStartingPitcher && <StartingPitcher />}
            </span>
          </PlayerNameLink>
        </Horizontal>
      </Cell>
      <GameStatsCells
        position={position}
        fantasyScore={fantasyScore}
        playerGameScore={
          position === 'pitching'
            ? player.pitcherPlayerGameScore
            : player.hitterPlayerGameScore
        }
      />
    </StatsTableRow>
  );
};

PlayerRow.fragments = {
  So5Appearance: gql`
    fragment PlayerRow_So5Appearance on So5Appearance {
      id
      anyCard {
        slug
      }
      so5Lineup {
        id
        so5LeaderboardContender {
          slug
        }
      }
      so5Fixture {
        slug
        type
      }
    }
  ` as TypedDocumentNode<PlayerRow_So5Appearance>,
  GameOfBaseball: gql`
    fragment PlayerRow_GameOfBaseball on GameOfBaseball {
      id
      date
      probablePitchers {
        slug
      }
    }
  ` as TypedDocumentNode<PlayerRow_GameOfBaseball>,
  BaseballPlayer: gql`
    fragment PlayerRow_BaseballPlayer on BaseballPlayer {
      slug
      displayName
      pitcherPlayerGameScore(gameId: $gameIdWithoutPrefix) {
        id
        score
        ...GameStatsCells_PlayerGameScoreInterface
      }
      hitterPlayerGameScore(gameId: $gameIdWithoutPrefix) {
        id
        score
        ...GameStatsCells_PlayerGameScoreInterface
      }
    }
    ${GameStatsCells.fragments.PlayerGameScoreInterface}
  ` as TypedDocumentNode<PlayerRow_BaseballPlayer>,
};
